import React, { Fragment, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { find, get, map, isEqual, compact } from 'lodash';
import { NetworkStatus, useLazyQuery } from '@apollo/client';
import {
  WorkPackage,
  WorkPackageFilterInput,
  PagingInput,
  WorkPackageSearchByEnum,
  WorkPackageFilterByTerm,
  WorkPackageSortByTerm,
  WorkPackageList,
  MilestoneFilterStatusFlag,
} from '@bechtel/types';
import {
  Box,
  Spinner,
  SpinnerSize,
  Heading,
  Button,
  ButtonAppearance,
  ButtonSize,
  Icon,
  IconSize,
  useModalState,
  Text,
  TextFieldSize,
} from '@bechtel/gds';
import { GET_WORKPACKAGES } from '@bechtel/client-gql';
import {
  WorkPackageItemCard,
  ErrorBoundary,
  useSessionStorage,
  CacheKeys,
  TextWithEllipsis,
  EllipsisPlacement,
  useDebounce,
} from '@bechtel/client-common';
import {
  ListStyled,
  NavLinkStyled,
  WorkPackageListContainerStyled,
  WorkPackageListHeader,
  SearchFieldStyled,
  SearchCanvasButtonStyled,
  SearchFieldComponentStyled,
  StyledIcon,
} from './styled';
import {
  drawerValues as defaultValues,
  DrawerValuesProps,
  SortDirection,
  filterByList,
  searchScopeList,
  searchByList,
} from './form-state';
// @ts-ignore: tsc declarations not recognising types/index.d.ts
import { ReactComponent as FilterIcon } from '@bechtel/client-assets/dist/images/filter.svg';
import { Drawer } from './drawer';
import { t, Trans } from '@lingui/macro';
import { NavigationType, useLocation, useNavigate, useNavigationType } from 'react-router-dom';

const wpLimit = 20;

const WorkPackages = () => {
  // NOTE: used for toggling advanced search drawer
  const didMount = useRef(false);

  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const { close, toggle, visible } = useModalState(false);
  const [workPackageList, setWorkPackageList] = useState<WorkPackage[]>([]);
  // NOTE: we need a useRef here otherwise route transitions will "reset" the state for drawerValues and fall back to the "defaultValues"
  // TODO: move this to local storage eventually to persist search settings across sessions
  const persistedDrawerValues = useRef(defaultValues);
  const [drawerValues, setDrawerValues] = useSessionStorage<DrawerValuesProps>(
    CacheKeys.WorkPackageDrawerValues,
    persistedDrawerValues.current || defaultValues
  );

  useEffect(() => {
    if (!location || !location.state || !location.state['drawerValues']) return;
    setDrawerValues(location.state['drawerValues']);
  }, [location, location.state]);

  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!location || !location.search || location.search == '') return;
    const params = new URLSearchParams(document.location.search.substring(1));
    const wpTag = params.get('tag');
    if (!wpTag) return;
    setSearchQuery(wpTag);
  }, [location, location.search]);

  const [totalWorkpackages, setTotalWorkpackages] = useState(0);
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const [loadWorkpackages, { loading, data, error, refetch, networkStatus, called, fetchMore }] = useLazyQuery<
    { workPackages: WorkPackageList },
    {
      filter: WorkPackageFilterInput;
      paging: PagingInput;
    }
  >(GET_WORKPACKAGES, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    onCompleted: (data) => {
      setTotalWorkpackages(data?.workPackages?.total);
    },
  });

  useEffect(() => {
    if (debouncedSearchQuery.length > 0 && debouncedSearchQuery.length < 3) return;
    loadWorkpackages({
      variables: {
        filter: {
          searchKeyword: debouncedSearchQuery.trim(),
          searchBy: drawerValues?.searchBy ? drawerValues?.searchBy : [{ type: WorkPackageSearchByEnum.WP_NUMBER }],
          filterBy:
            drawerValues?.filterBy &&
            ![
              MilestoneFilterStatusFlag.READY_TO_CLAIM_CONNECTIONS,
              MilestoneFilterStatusFlag.PENDING_APPROVAL,
              MilestoneFilterStatusFlag.REJECTED,
            ].includes(drawerValues?.filterBy)
              ? drawerValues?.filterBy
              : null,
          filterByMilestonesStatusFlags:
            drawerValues?.filterBy &&
            [
              MilestoneFilterStatusFlag.READY_TO_CLAIM_CONNECTIONS,
              MilestoneFilterStatusFlag.PENDING_APPROVAL,
              MilestoneFilterStatusFlag.REJECTED,
            ].includes(drawerValues?.filterBy)
              ? [drawerValues?.filterBy]
              : [],
          sortBy: drawerValues?.sortBy ? drawerValues?.sortBy : WorkPackageSortByTerm.WP_NUMBER,
          assignedToMe: drawerValues?.searchScope,
          sortOrder: drawerValues?.sortDir ? drawerValues?.sortDir : SortDirection.DESC,
        },
        paging: {
          cursor: '1',
          limit: wpLimit,
        },
      },
    });
  }, [debouncedSearchQuery, drawerValues, loadWorkpackages]);

  const onDrawerSubmit = useCallback(
    (values: DrawerValuesProps) => {
      setDrawerValues(() => {
        persistedDrawerValues.current = values;
        return values;
      });
    },
    [setDrawerValues]
  );

  // NOTE: allows us to safely access "data.workPackages.items" during all lifecycles
  const workPackageListItems = useMemo(() => {
    return get(data, 'workPackages.items', []);
  }, [data]);
  const totalPages = totalWorkpackages ? Math.ceil(totalWorkpackages / wpLimit) : 0;
  const wpListCursor = get(data, 'workPackages.cursor', 1);
  useEffect(() => {
    setWorkPackageList(workPackageListItems);
  }, [workPackageListItems]);

  // NOTE: allows for client side searching, filtering, and sorting
  // TODO : Implement client side filtering and searching when the result is less than page size and filters from drawer values are same.
  //const searched = filter(defaultWorkPackages, searchByFn(drawerValues.searchBy, searchQuery));
  //const sorted = orderBy(defaultWorkPackages, sortByFn(drawerValues.sortBy), drawerValues.sortDir);

  const handleScroll = (e) => {
    if (
      e.target.scrollTop + e.target.clientHeight + 200 >= e.target.scrollHeight &&
      wpListCursor <= totalPages &&
      !loading
    ) {
      fetchMore({
        variables: {
          paging: {
            cursor: wpListCursor.toString(),
            limit: wpLimit,
          },
        },
      });
    }
  };

  useEffect(() => {
    if (workPackageList?.length > 0) {
      if (location.pathname === '/work-packages') {
        if (navigationType === NavigationType.Pop) {
          navigate(-1);
        } else {
          const [workPackage] = workPackageList;
          const encodedId = encodeURIComponent(workPackage.id);
          navigate(`${encodedId}`);
        }
      }
    }
  }, [workPackageList, location.pathname, navigationType, navigate]);

  // FIXME: adjust logic once these are moved away from mock data

  const searchingByLabel = map(drawerValues.searchBy, (search: any) => {
    return get(find(searchByList, { searchByTerm: { type: search.type } }), 'label');
  });

  const filteringByLabel = get(find(filterByList, { value: drawerValues.filterBy }), 'label');
  //TODO uncomment if PO want sort byoption in heading
  //const sortingByLabel = get(find(sortByList, { value: drawerValues.sortBy }), 'label');
  const searchingLabel = get(find(searchScopeList, { value: drawerValues.searchScope }), 'label');

  return (
    <Fragment>
      <WorkPackageListContainerStyled data-testid="workpackage-list-container" isDrawerOpen={visible}>
        <WorkPackageListHeader>
          <Box px={{ sm: '300' }} pt={{ sm: '500' }} py={{ sm: '550' }}>
            <Heading weight="600" size="600">
              {drawerValues?.searchScope ? t`My ` : t`All `} {t`Work Packages`}{' '}
              {totalWorkpackages ? `(${totalWorkpackages})` : ''}
            </Heading>
          </Box>
          <SearchFieldStyled>
            <SearchFieldComponentStyled
              size={TextFieldSize.LARGE}
              iconSize={IconSize.MEDIUM}
              placeholder={t`Search`}
              type="search"
              onChange={(e) => {
                setSearchQuery(e.target.value);
              }}
              value={searchQuery}
            />
            <Box
              ml={{ sm: '400' }}
              css={`
                max-width: ${({ theme }) => theme.spacings[800]};
              `}
            >
              <SearchCanvasButtonStyled
                data-testid="button-search-canvas"
                isDefaultFilterChange={!isEqual(drawerValues, defaultValues)}
                onClick={toggle}
                appearance={ButtonAppearance.SECONDARY}
                size={ButtonSize.LARGE}
              >
                <StyledIcon
                  icon="sliders-v"
                  size={IconSize.LARGE}
                  fillColor={!isEqual(drawerValues, defaultValues) ? 'white' : ''}
                />
              </SearchCanvasButtonStyled>
            </Box>
          </SearchFieldStyled>

          <Box flexDirection={{ sm: 'column' }} pr={{ sm: '600' }}>
            <Box my={{ sm: '200' }}>
              <Text size="250" weight="600">
                <Trans>Searching</Trans> :&nbsp;
                <Text weight="400" data-testid="searching-text">
                  {searchingLabel}
                </Text>
              </Text>
            </Box>
            {searchingByLabel && searchingByLabel.length > 0 && (
              <Box>
                <TextWithEllipsis size="250" weight="600" ellipsisPlacement={EllipsisPlacement.END}>
                  <Trans>Searching By</Trans>:&nbsp;
                  <Text weight="400" title={compact(searchingByLabel)?.join(' , ')} data-testid="searching-by-text">
                    {searchingByLabel.length > 0
                      ? searchingByLabel.length === searchByList.length
                        ? t`Search All`
                        : compact(searchingByLabel)?.join(' , ')
                      : ''}
                  </Text>
                </TextWithEllipsis>
              </Box>
            )}
            {filteringByLabel && filteringByLabel.length > 0 && (
              <Box my={{ sm: '200' }}>
                <Text size="250" weight="600">
                  <Trans> Filtering By</Trans>:&nbsp;
                  <Text weight="400" data-testid="filter-text">
                    {filteringByLabel}
                  </Text>
                </Text>
              </Box>
            )}
            {/*TODO uncomment below code when Sort by option is required to shpw */}
            {/* <Box my={{ sm: '200' }}>
              <Text size="250" weight="600">
                Sorting By:&nbsp;
                <Text weight="400">
                  {sortingByLabel} ({upperCase(drawerValues.sortDir)})
                </Text>
              </Text>
            </Box> */}
          </Box>
        </WorkPackageListHeader>

        <ListStyled
          onScroll={(e) => {
            handleScroll(e);
          }}
        >
          {workPackageList.length > 0 &&
            map(workPackageList, (wp: WorkPackage) => {
              const encodedId = encodeURIComponent(wp.id);
              return (
                <NavLinkStyled to={`/work-packages/${encodedId}`} key={encodedId}>
                  <ErrorBoundary>
                    <WorkPackageItemCard {...wp} />
                  </ErrorBoundary>
                </NavLinkStyled>
              );
            })}
          {(networkStatus === NetworkStatus.refetch || loading) && (
            <Box
              p={{ sm: '500' }}
              alignItems={{ sm: 'center' }}
              justifyContent={{ sm: 'center' }}
              flexGrow={{ sm: 1 }}
              css={workPackageList.length === 0 ? 'height: 100%;' : ''}
            >
              <Spinner size={SpinnerSize.LARGE} />
            </Box>
          )}
          {!loading && error && (
            <Box
              alignItems={{ sm: 'center' }}
              justifyContent={{ sm: 'center' }}
              flexGrow={{ sm: 1 }}
              flexDirection={{ sm: 'column' }}
              p={{ sm: '500' }}
            >
              <Box pb={{ sm: '500' }}>
                <Text>
                  <Trans>There was a problem loading Work Packages. Please click the button below to try again.</Trans>
                </Text>
              </Box>
              <Button onClick={() => refetch()}>
                <Box mr={{ sm: '400' }}>
                  <Icon icon="undo" />
                </Box>
                <Trans>Retry</Trans>
              </Button>
            </Box>
          )}
          {called && !loading && !error && workPackageList.length === 0 && (
            <Box
              flexDirection={{ sm: 'column' }}
              alignItems={{ sm: 'center' }}
              pt={{ sm: '900' }}
              data-testid="workpackage-list-empty"
            >
              <Heading size="400" weight="600" color="grey.600">
                <Trans> No work packages found.</Trans>
              </Heading>
              <Box mt={{ sm: '700' }} mb={{ sm: '600' }} px={{ sm: '600' }}>
                <Text
                  size="400"
                  family="graphik"
                  color="grey.600"
                  //  line height is required because when sentence wraps in two lines gap between both lines are required.
                  style={{ textAlign: 'center', lineHeight: '1.57' }}
                >
                  <Trans> Try changing your search or filters to view a work package.</Trans>
                </Text>
              </Box>
              <FilterIcon style={{ width: '124px', height: '110px' }} />
              {drawerValues?.searchScope && (
                <Box mt={{ sm: '500' }}>
                  <Button
                    appearance={ButtonAppearance.SECONDARY}
                    onClick={() =>
                      setDrawerValues(() => {
                        persistedDrawerValues.current = {
                          ...drawerValues,
                          searchScope: false,
                          filterBy: WorkPackageFilterByTerm.ALL,
                        };
                        return persistedDrawerValues.current;
                      })
                    }
                  >
                    <Box px={{ sm: '700' }} py={{ sm: '700' }}>
                      <Text color="blue.800" weight="600">
                        <Trans>View All Work Packages</Trans>
                      </Text>
                    </Box>
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </ListStyled>
      </WorkPackageListContainerStyled>
      <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
        {visible && <Drawer close={close} initialValues={drawerValues} onSubmit={onDrawerSubmit} />}
      </Suspense>
    </Fragment>
  );
};

export default WorkPackages;
